import * as t from 'io-ts';

import {UnreachableError} from '../../../libs/shared/helpers/src/types/customErrors';
import {
  AnalyticsMetric,
  AnalyticsMetricTypesEnum,
  parseAnalyticsGraphMetric,
  parseAnalyticsHeatMapMetric,
  parseAnalyticsScalarMetric,
  parseAnalyticsTableMetric,
  RAnalyticsMetricDirty,
  RAnalyticsMetricsKeysDirty,
} from './analyticsMetricsModel';
import {makeApiType, makeEnumRuntimeType, RDateTimeString} from './apiTypes';

/**
 * Analytics models and parsers for jobs.
 */

/*
 * Runtime types.
 */

export enum AnalyticsJobStatusEnum {
  STARTING = 'starting',
  REPLAYING = 'replaying',
  DONE = 'done',
  ERROR = 'error',
}

const RAnalyticsJobProgressEventDirty = t.intersection([
  t.type({
    uid: t.string,
    progress: t.number,
    status: makeEnumRuntimeType(AnalyticsJobStatusEnum),
  }),
  t.partial({
    startTime: RDateTimeString,
    endTime: RDateTimeString,
  }),
]);

const RAnalyticsJobResultDirty = t.intersection([
  RAnalyticsJobProgressEventDirty,
  t.type({
    metrics: t.readonlyArray(RAnalyticsMetricDirty),
    timedOutMetrics: t.array(RAnalyticsMetricsKeysDirty),
  }),
]);

/*
 * Type.
 */

export interface AnalyticsJobProgressEvent extends t.TypeOf<typeof RAnalyticsJobProgressEventDirty> {}

export interface AnalyticsJobResult extends t.TypeOf<typeof RAnalyticsJobResultDirty> {}

/*
 * IO methods.
 */

function parseAnalyticsJob(src: any): AnalyticsJobResult {
  return {
    uid: src.uid,
    progress: src.progress,
    status: src.status,
    metrics: Object.values(src.metrics || {}).reduce((acc: ReadonlyArray<AnalyticsMetric>, value) => {
      const parsedValue = parseJobsMetric(value);
      return [...acc, parsedValue];
    }, []),
    timedOutMetrics: src.timedOutMetrics,
    startTime: src.startTime,
    endTime: src.endTime,
  };
}

function parseAnalyticsJobProgressEvent(src: any): AnalyticsJobProgressEvent {
  return {
    uid: src.job_uid,
    progress: src.progress,
    status: src.status,
    startTime: src.startTime,
    endTime: src.endTime,
  };
}

function parseJobsMetric(src: any): AnalyticsMetric {
  switch (src.metric_type) {
    case AnalyticsMetricTypesEnum.SCALAR:
      return parseAnalyticsScalarMetric(src);

    case AnalyticsMetricTypesEnum.TABLE:
      return parseAnalyticsTableMetric(src);

    case AnalyticsMetricTypesEnum.TIME_GRAPH:
      return parseAnalyticsGraphMetric(src);

    case AnalyticsMetricTypesEnum.HEAT_MAP:
      return parseAnalyticsHeatMapMetric(src);

    default:
      throw new UnreachableError(`No parser found for metric type ${src.metric_type}` as never);
  }
}

/*
 * API type.
 */

export const ApiAnalyticsJobResult = makeApiType(
  'analyticsJobResult',
  RAnalyticsJobResultDirty,
  parseAnalyticsJob,
);

export const ApiAnalyticsJobProgressEvent = makeApiType(
  'analyticsJobProgressEvent',
  RAnalyticsJobProgressEventDirty,
  parseAnalyticsJobProgressEvent,
);
